import type { AppState } from 'src/reducers';
import { createSelectorCreator, lruMemoize } from 'reselect';
import { get, isEqual } from 'lodash';
import VisualizeSlice from './Visualize.slice';
import type { Metric } from 'src/common-ui/components/Macros/Macros.types';
import type { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import { STYLE_COLOR_ID } from 'src/utils/Domain/Constants';
import { macroConfigItemToMetric } from '../MacroGridPair/MacroGridPair.selectors';
import type { WrapperOwnProps } from './Visualize.container';
import type { Scope } from 'src/types/Scope';
import type { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import type { BasicPivotItem, Pivot, WorklistInfo } from 'src/worker/pivotWorker.types';
import type { ViewDataState } from 'src/types/Domain';
import type { StylePreviewData } from 'src/components/StylePreview/StylePreview.types';
import { sharedDataLens } from 'src/services/lenses/lenses';
import type { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import { isWorklistActive } from 'src/utils/Functions/epicsFunctions';

export interface StateSelection extends ReturnType<typeof VisualizeSlice>, WrapperOwnProps {
  scopeStart: Scope['start'];
  worklistItems?: WorklistInfo[];
  adornments: AdornmentType[];
  isWorklistActive: boolean;
}
export interface VisualizeStateProjection {
  loading?: boolean;
  data?: Pivot;
  scopeStart: Scope['start'];
  selectedItemPreviewData: StylePreviewData;
  subheaderSlice: SubheaderSlice;
  allStylecolorIds: string[];
  onSelectStyle: (id: string) => void;
  summaryData: Metric[] | undefined;
  worklistItems?: WorklistInfo[];
  graphsViewDefn: TenantConfigViewData | undefined;
  dontFilterSwatches?: boolean;
  adornments: AdornmentType[];
  lineGraphDefnId: string;
  isWorklistActive: boolean;
  graphData: BasicPivotItem[][][] | undefined;
  graphCategories: string[] | undefined;
  graphState: ViewDataState;
}

export function selectState(state: AppState, ownProps: WrapperOwnProps): StateSelection {
  // FIXME: be wary, the spreads here will create new objects and break object reference links
  const scopeStart = state.scope.scope.start;
  const worklistItems = sharedDataLens.get(state).worklist;
  const viewState = state.pages.assortmentBuild.visualize;
  const adornments: AdornmentType[] = get(viewState.vizViewDefns?.grid, 'adornments', []);

  return {
    scopeStart,
    worklistItems,
    adornments,
    isWorklistActive: isWorklistActive(state),
    ...state.pages.assortmentBuild.visualize,
    ...ownProps,
  };
}

const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual);
export default createDeepEqualSelector(selectState, (stateSelection) => {
  const { vizViewDefns, summaryData, styles } = stateSelection;
  const graphsViewDefn = vizViewDefns?.graphs;
  // TODO: do this somewhere else
  let renderedSummaryData: Metric[] = [];
  if (summaryData && summaryData.length > 0 && vizViewDefns && vizViewDefns.summary) {
    renderedSummaryData = vizViewDefns.summary.view.map((macroConfigItem) => {
      return macroConfigItemToMetric(macroConfigItem, summaryData[0]);
    });
  }

  return {
    ...stateSelection,
    graphsViewDefn,
    allStylecolorIds: styles.map((x) => x[STYLE_COLOR_ID]),
    summaryData: renderedSummaryData,
    lineGraphDefnId: stateSelection.viewDefns[2],
  };
});
